// Libs
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { useTheme, withStyles } from '@material-ui/core/styles';

// Components
import Box from '@material-ui/core/Box';
import MobileStepper from '@material-ui/core/MobileStepper';
import Button from '@material-ui/core/Button';

// Actions
import { nextCheck, previousCheck } from 'REDUCERS/navSlice';

// Utils
import { checkList } from 'SRC/store';
import { NARROW_LAYOUT, RUNNING, SUCCESS_LEVEL, PENDING } from 'UTILS/constants';
import useTranslator from 'UTILS/hooks/useTranslator';
import { onTrackedClick } from 'UTILS/callbacks';
import { BLOCKING_CHECKS } from 'CHECKS';
import translations from './locales.json';

const DOT_SIZE = '12px';
const DOT_SPACE = '8px';

const CustomButton = withStyles({
  disabled: {
    visibility: 'hidden',
  },
})(Button);

const NavBar = () => {
  const isSmallScreen = useSelector(state => state.app.layout === NARROW_LAYOUT);
  const [translate] = useTranslator(translations);
  const theme = useTheme();
  const dispatch = useDispatch();
  const currentCheck = useSelector(state => state.nav.currentCheck);
  const currentCheckIndex = checkList.indexOf(currentCheck);
  const isSuccessFull = useSelector(state => state.checks[currentCheck]?.level === SUCCESS_LEVEL);
  const isCurrentCheckRunning = useSelector(state => state.checks[currentCheck]?.stage === RUNNING);
  const isCheckPending = useSelector(state => checkList.some(checkLabel => state.checks[checkLabel].stage === PENDING));
  const lastCheck = checkList[checkList.length - 1];
  const checks = useSelector(state => state.checks);
  const isNextFullySuccessful = Object.values(checks).slice(currentCheckIndex + 1).every(check => check?.level === SUCCESS_LEVEL);
  const handlePreviousClick = () => {
    onTrackedClick('nav_previous', currentCheck);
    dispatch(previousCheck());
  };
  const handleNextClick = () => {
    onTrackedClick('nav_next', currentCheck);
    dispatch(nextCheck());
  };

  const StyledDotStepper = useMemo(() => {
    return withStyles({
      dot: {
        width: DOT_SIZE,
        height: DOT_SIZE,
        margin: `0 calc(${DOT_SPACE} / 2)`,
      },
      // Use absolute position to center the dots even with asymmetric buttons around it
      // We put its left at 50% minus half its with (which can be computed from styles defined above)
      dots: {
        position: 'absolute',
        left: `calc(50% - (((${DOT_SIZE} * ${checkList.length}) + (${DOT_SPACE} * ${checkList.length - 1})) / 2 ))`,
      },
    })(MobileStepper);
  }, [checkList]);

  const classes = cx('navBar-container', {
    'navBar-container_large': !isSmallScreen,
  });
  return (
    <Box className={classes} >
      <StyledDotStepper
        steps={checkList.length}
        activeStep={currentCheckIndex}
        backButton={<CustomButton disabled={isCurrentCheckRunning} color='primary' onClick={handlePreviousClick}>{translate('previous')}</CustomButton>}
        nextButton={
          <CustomButton disabled={isCurrentCheckRunning || (BLOCKING_CHECKS.includes(currentCheck) && !isSuccessFull) || (isCheckPending && currentCheck === lastCheck)} color='primary' onClick={handleNextClick}>
            {currentCheck === lastCheck || isNextFullySuccessful ? translate('summary') : isSuccessFull ? translate('next') : translate('skip') }
          </CustomButton>
        }
        style={{ position: 'static', backgroundColor: theme.palette.background.paper }}
      />
    </Box>
  );
};

export default NavBar;
